import { useRef, useState } from "react";
import { useAuth } from "../../../contexts/auth/Context";
import { decodeUserInfo } from "../../../utils/user";
import { useModuleEnabled } from "../../../hooks/gym/useModuleEnabled";
import { useUserProfile } from "../../../hooks/user/useUserProfile";
import { useCurrentGym } from "../../../hooks/gym/useCurrentGym";
import { useUploadGymLogo } from "../../../hooks/gym/useUploadGymLogo";
import { useToast } from "../../../contexts/toast/Context";
import { Avatar } from "../../common/ui/avatar";
import { getResourceUrl } from "../../../utils/images";
import { Tabs } from "../../common/ui/tabs";
import { GymEmailConfig } from "./emailConfig";
import { GymGatewayConfig } from "./gatewayConfig";
import { GymPaymentMethods } from "./paymentMethods";
import { GymPlanCategories } from "./planCategories";
import { GymTaxInfo } from "./taxInfo";
import { GymOperatingHours } from "./operatingHours";
import { ExpensesConfigSection } from "./expensesConfigSection";
import { ClientAppConfigSection } from "./clientAppConfigSection";

const InfoCell = ({
  label,
  value,
  isLoading,
  className = "",
}: {
  label: string;
  value: string | null | undefined;
  isLoading?: boolean;
  className?: string;
}) => {
  return (
    <div className={`space-y-1 min-w-0 ${className}`}>
      <label className="text-label">{label}</label>
      <div className="card-section">
        {isLoading ? (
          <p className="text-size-sm text-global-tertiary italic">
            Cargando información...
          </p>
        ) : value ? (
          <p className="text-size-sm font-medium text-global-primary wrap-break-word">
            {value}
          </p>
        ) : (
          <span className="text-global-tertiary italic">No disponible</span>
        )}
      </div>
    </div>
  );
};

export const GymInfo = () => {
  const { token } = useAuth();
  const userInfo = decodeUserInfo(token);
  const { userProfile } = useUserProfile();
  const { data: gym, isLoading: gymLoading } = useCurrentGym();
  const { uploadLogo, isCompressing, isUploading } = useUploadGymLogo();
  const { showToast, removeToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isManager = userInfo.role === "MANAGER";

  const logoSrc = gym?.logoKey ? getResourceUrl(gym.logoKey) : "";

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const loadingId = showToast("Cargando imagen de negocio...", "loading");
    try {
      await uploadLogo(file);
      removeToast(loadingId);
      showToast("Imagen de negocio cargada correctamente", "success");
    } catch {
      removeToast(loadingId);
      showToast("Error al cargar la imagen de negocio", "error");
    }
  };

  return (
    <div className="rounded-xl bg-global-primary border border-global-primary shadow-global-card overflow-hidden">
      {/* Gradient banner */}
      <div className="relative h-28 btn-global-primary-gradient overflow-hidden">
        <div className="absolute inset-0 gradient-accent-overlay opacity-40 pointer-events-none" />
      </div>

      {/* Logo + identity */}
      <div className="-mt-14 flex flex-col items-center px-4 pb-5">
        <div className="relative mb-3">
          <div
            className="w-28 h-28 rounded-full overflow-hidden bg-global-overlay-secondary"
            style={{
              boxShadow:
                "0 0 0 4px var(--bg-primary), 0 0 0 6px var(--border-primary)",
            }}
          >
            <Avatar
              src={logoSrc}
              alt={gym?.name ?? "Negocio"}
              fallback={
                logoSrc
                  ? (gym?.name?.slice(0, 2).toUpperCase() ?? "G")
                  : "No hay logo"
              }
              className="w-full h-full"
            />
          </div>
          {isManager && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                className="hidden"
                onChange={handleLogoChange}
                disabled={isCompressing || isUploading}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isCompressing || isUploading || gymLoading}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity text-white text-size-2xs font-medium text-center px-2 cursor-pointer"
                title="Cambiar imagen de negocio"
              >
                {isCompressing
                  ? "Comprimiendo..."
                  : isUploading
                    ? "Subiendo..."
                    : "Cambiar logo"}
              </button>
            </>
          )}
        </div>

        <h2 className="text-title-section text-center">
          {gymLoading ? (
            <span className="text-global-tertiary italic font-normal">
              Cargando...
            </span>
          ) : (
            (gym?.name ??
            userInfo.gymName ?? (
              <span className="text-global-tertiary italic font-normal">
                Sin nombre
              </span>
            ))
          )}
        </h2>

        <span className="mt-2 inline-flex items-center px-3 py-0.5 rounded-full text-size-2xs font-semibold bg-global-overlay-secondary text-global-secondary border border-global-secondary tracking-wide uppercase">
          {userProfile?.role ?? userInfo.role ?? "Staff"}
        </span>
      </div>

      {/* Divider */}
      <div className="border-t border-global-primary" />

      {/* Info grid */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <InfoCell label="RUT" value={gym?.rut ?? null} isLoading={gymLoading} />
        <InfoCell
          label="Razón social"
          value={gym?.legalName ?? null}
          isLoading={gymLoading}
        />
        <InfoCell
          label="Dirección"
          value={gym?.address ?? null}
          isLoading={gymLoading}
        />
        <InfoCell
          label="País"
          value={gym?.country ?? null}
          isLoading={gymLoading}
        />
        <InfoCell
          label="Zona horaria"
          value={gym?.timezone ?? null}
          isLoading={gymLoading}
        />
      </div>
    </div>
  );
};

export const GymInfoWithEmailConfig = () => {
  const { token } = useAuth();
  const userInfo = decodeUserInfo(token);
  const isManager = userInfo.role === "MANAGER";
  const planSalesEnabled = useModuleEnabled("planSales");
  const onlinePaymentsEnabled = useModuleEnabled("onlinePayments");
  const accessControlEnabled = useModuleEnabled("accessControl");
  const expensesEnabled = useModuleEnabled("expenses");
  const { data: currentGym } = useCurrentGym();
  const isB2B = currentGym?.businessType === "B2B";

  // Pestaña inicial por URL (?tab=pasarela al volver del onboarding OAuth de
  // Mercado Pago); sin el parámetro, la de siempre.
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get("tab") ?? "impuestos",
  );

  const tabs = [
    {
      id: "impuestos",
      label: "Impuestos",
      content: <GymTaxInfo />,
    },
    ...(planSalesEnabled
      ? [
          {
            id: "categorias",
            label: "Categorías de plan",
            content: <GymPlanCategories />,
          },
        ]
      : []),
    ...(isManager
      ? [
          ...(accessControlEnabled
            ? [
                {
                  id: "horarios",
                  label: "Horarios",
                  content: <GymOperatingHours />,
                },
              ]
            : []),
          {
            id: "pagos",
            label: "Métodos de cobro",
            content: <GymPaymentMethods />,
          },
          ...(expensesEnabled
            ? [
                {
                  id: "gastos",
                  label: "Gastos",
                  content: <ExpensesConfigSection />,
                },
              ]
            : []),
          ...(!isB2B
            ? [
                {
                  id: "app",
                  label: "App del cliente",
                  content: <ClientAppConfigSection />,
                },
              ]
            : []),
          ...(planSalesEnabled
            ? [
                {
                  id: "correo",
                  label: "Correo electrónico",
                  content: <GymEmailConfig />,
                },
              ]
            : []),
          ...(onlinePaymentsEnabled
            ? [
                {
                  id: "pasarela",
                  label: "Pagos Online",
                  content: <GymGatewayConfig />,
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-compact">
      <GymInfo />
      <div className="card-compact">
        <Tabs
          tabs={tabs}
          activeTab={
            tabs.some((t) => t.id === activeTab) ? activeTab : "impuestos"
          }
          onTabChange={setActiveTab}
        />
      </div>
    </div>
  );
};
